import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { NbButton } from "../components/nb";
import { QrCode, Sparkles, ShieldCheck, MessageCircle, Search, MapPin, ArrowRight, BadgeCheck, Heart } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1760143522247-e7b2454536da?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const EVENT = "https://images.unsplash.com/photo-1724866976376-4b217d29a462?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

export default function Home() {
  return (
    <div className="min-h-screen bg-[#FCFCFA]">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 animate-fade-up">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-6">
            <span className="w-8 h-px bg-[#D4AF37]" /> Real-world dating, refined
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-stone-900">
            Your profile,<br /><span className="italic text-[#8B1E3F]">one scan</span> away.
          </h1>
          <p className="text-lg text-stone-600 mt-7 max-w-lg leading-relaxed">
            Craft an elegant dating profile and share it instantly with your personal QR code. Designed for singles events, members' bars, and meaningful networking.
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <NbButton as={Link} to="/register" data-testid="hero-cta-register" className="px-8 py-3.5">Create your code <ArrowRight className="w-4 h-4" /></NbButton>
            <NbButton as={Link} to="/pricing" variant="outline" data-testid="hero-cta-pricing" className="px-8 py-3.5">View membership</NbButton>
          </div>
          <div className="flex items-center gap-8 mt-12">
            <div><div className="font-display text-3xl text-stone-900">12k+</div><div className="text-xs uppercase tracking-wider text-stone-500 mt-1">Members</div></div>
            <div className="w-px h-10 bg-stone-200" />
            <div><div className="font-display text-3xl text-stone-900">50k+</div><div className="text-xs uppercase tracking-wider text-stone-500 mt-1">Scans</div></div>
            <div className="w-px h-10 bg-stone-200" />
            <div><div className="font-display text-3xl text-stone-900">3k+</div><div className="text-xs uppercase tracking-wider text-stone-500 mt-1">Matches</div></div>
          </div>
        </div>

        <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "0.12s" }}>
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(28,25,23,0.18)] aspect-[4/5] max-w-md ml-auto">
            <img src={HERO} alt="Elegant couple" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </div>
          {/* Floating VIP code */}
          <div className="absolute -bottom-6 -left-2 sm:left-6 vip-texture rounded-2xl p-4 w-44 shadow-2xl text-white">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] tracking-[0.2em] text-[#D4AF37]">VIP CODE</span>
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            </div>
            <div className="bg-white rounded-lg p-2 flex items-center justify-center">
              <QrCode className="w-20 h-20 text-[#1A1A1A]" strokeWidth={1.4} />
            </div>
            <div className="mt-2 text-center">
              <div className="font-display text-sm flex items-center justify-center gap-1">Jordan <BadgeCheck className="w-3.5 h-3.5 text-[#D4AF37]" fill="#D4AF37" stroke="#1A1A1A" /></div>
            </div>
          </div>
          <div className="absolute -top-4 right-2 bg-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-sm font-medium text-stone-800">
            <Heart className="w-4 h-4 text-[#8B1E3F]" fill="#8B1E3F" /> It's a match
          </div>
        </div>
      </section>

      {/* Promo video */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 py-8">
        <div className="relative rounded-[2rem] overflow-hidden shadow-[0_24px_70px_rgba(28,25,23,0.16)] bg-stone-900">
          <video
            src="https://customer-assets.emergentagent.com/job_cupid-qr-1/artifacts/li7m3bap_QRCupid%20Promo.mp4"
            autoPlay
            muted
            loop
            playsInline
            controls
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-3">The experience</div>
          <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-stone-900">Three steps to your next real connection</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { icon: Heart, t: "Build your profile", d: "Add photos, a thoughtful bio, your interests and what you're looking for.", n: "01" },
            { icon: QrCode, t: "Get your VIP code", d: "Your personal QR code is generated instantly. Download or print it to wear.", n: "02" },
            { icon: MessageCircle, t: "Match & meet", d: "Someone scans, you connect. Message, match and meet in real life.", n: "03" },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-2xl border border-stone-100 shadow-[0_8px_30px_rgb(28,25,23,0.05)] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(28,25,23,0.10)]">
              <div className="flex items-center justify-between mb-5">
                <div className="w-12 h-12 rounded-full bg-[#F3E8E6] flex items-center justify-center"><s.icon className="w-5 h-5 text-[#8B1E3F]" /></div>
                <span className="font-display text-3xl text-stone-200">{s.n}</span>
              </div>
              <h3 className="font-display text-xl font-medium text-stone-900 mb-2">{s.t}</h3>
              <p className="text-stone-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature split */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(28,25,23,0.12)] min-h-[380px]">
            <img src={EVENT} alt="Networking event" className="w-full h-full object-cover" />
          </div>
          <div className="flex flex-col justify-center gap-6">
            {[
              { icon: Search, t: "Curated discovery", d: "Filter by age, distance, interests, online status and more. Premium unlocks advanced filters and who liked you." },
              { icon: ShieldCheck, t: "Verified & discreet", d: "Verification badges, blocking, reporting and human moderation keep the club genuine." },
              { icon: MapPin, t: "Made for the room", d: "Members' bars, mixers and networking nights — skip the awkward number exchange entirely." },
            ].map((f, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-11 h-11 rounded-full bg-[#1C1917] flex items-center justify-center flex-shrink-0"><f.icon className="w-5 h-5 text-[#D4AF37]" /></div>
                <div>
                  <h3 className="font-display text-xl font-medium text-stone-900">{f.t}</h3>
                  <p className="text-stone-600 mt-1 leading-relaxed">{f.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="vip-texture rounded-[2rem] px-8 py-16 md:py-24 text-center text-white relative overflow-hidden">
          <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-5" />
          <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight max-w-2xl mx-auto">Become unforgettable at your next event</h2>
          <p className="text-stone-300 mt-5 max-w-xl mx-auto leading-relaxed">Join thousands turning real-world moments into lasting connections.</p>
          <NbButton as={Link} to="/register" variant="accent" data-testid="cta-bottom-register" className="mt-9 px-9 py-3.5">Get your free code <ArrowRight className="w-4 h-4" /></NbButton>
        </div>
      </section>

      <Footer />
    </div>
  );
}
